import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material'
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material'
import { authService } from '../../services/api'
import { ResetPasswordRequest } from '../../types/api'
import AuthPageWrapper from './AuthPageWrapper'

const ResetPasswordForm: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [generalError, setGeneralError] = useState<string>('')

  // Verifică dacă avem token-ul necesar
  if (!token) {
    return (
      <AuthPageWrapper>
        <Paper
          elevation={0}
          sx={{
            padding: 3,
            width: '100%',
            maxWidth: '380px',
            borderRadius: 8,
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: 'none',
            
            transition: 'border-color 0.3s ease',
            '&:hover': {
              border: '1px solid rgba(255, 255, 255, 0.2)',
            },
            
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
          }}
        >
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              backgroundColor: 'rgba(244, 67, 54, 0.15)',
              color: '#F44336',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              '& .MuiAlert-icon': {
                color: '#F44336'
              }
            }}
          >
            Link invalid sau expirat
          </Alert>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#FFFFFF',
              fontSize: '20px',
              fontWeight: 500,
              mb: 2
            }}
          >
            Link de resetare invalid
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px',
              mb: 3
            }}
          >
            Acest link de resetare a parolei este invalid sau a expirat. Vă rugăm să solicitați unul nou.
          </Typography>
          
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            fullWidth
            sx={{
              py: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#FFFFFF',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '16px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            Înapoi la autentificare
          </Button>
        </Paper>
      </AuthPageWrapper>
    )
  }

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {}

    // Validare parolă nouă
    if (!formData.newPassword) {
      newErrors.newPassword = 'Parola este obligatorie'
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Parola trebuie să aibă cel puțin 8 caractere'
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Parola trebuie să conțină cel puțin o literă mică, o literă mare și o cifră'
    }

    // Validare confirmare parolă
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmarea parolei este obligatorie'
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Parolele nu se potrivesc'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }))
    
    // Șterge eroarea pentru câmpul modificat
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
    
    // Șterge eroarea generală când utilizatorul începe să scrie
    if (generalError) {
      setGeneralError('')
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setGeneralError('')

    try {
      const request: ResetPasswordRequest = {
        token,
        new_password: formData.newPassword
      }
      
      await authService.resetPassword(request)
      setIsSuccess(true)
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Eroare la resetarea parolei'
      setGeneralError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <AuthPageWrapper>
        <Paper
          elevation={0}
          sx={{
            padding: 3,
            width: '100%',
            maxWidth: '380px',
            borderRadius: 8,
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: 'none',
            
            transition: 'border-color 0.3s ease',
            '&:hover': {
              border: '1px solid rgba(255, 255, 255, 0.2)',
            },
            
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
          }}
        >
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              backgroundColor: 'rgba(76, 175, 80, 0.15)',
              color: '#4CAF50',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              '& .MuiAlert-icon': {
                color: '#4CAF50'
              }
            }}
          >
            Parolă resetată cu succes!
          </Alert>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#FFFFFF',
              fontSize: '20px',
              fontWeight: 500,
              mb: 2
            }}
          >
            Parolă actualizată
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px',
              mb: 3
            }}
          >
            Parola a fost resetată cu succes. Vă puteți autentifica acum cu noua parolă.
          </Typography>
          
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            fullWidth
            sx={{
              py: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#FFFFFF',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '16px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            Mergi la autentificare
          </Button>
        </Paper>
      </AuthPageWrapper>
    )
  }

  return (
    <AuthPageWrapper>
      <Paper
        elevation={0}
        sx={{
          padding: 3,
          width: '100%',
          maxWidth: '320px',
          borderRadius: 8,
          background: 'transparent',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'none',
          
          transition: 'border-color 0.3s ease',
          '&:hover': {
            border: '1px solid rgba(255, 255, 255, 0.2)',
          },
          
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              color: '#FFFFFF',
              fontSize: '24px',
              fontWeight: 500,
              marginBottom: 1,
              letterSpacing: '0.5px'
            }}
          >
            Resetare parolă
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px'
            }}
          >
            Introduceți noua parolă
          </Typography>
        </Box>

        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Parolă nouă"
              type={showPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleInputChange('newPassword')}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  paddingLeft: '50px',
                  '& input': {
                    '&:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                      transition: 'background-color 5000s ease-in-out 0s !important',
                    },
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Confirmă parola"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      disabled={isLoading}
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  paddingLeft: '50px',
                  '& input': {
                    '&:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                      transition: 'background-color 5000s ease-in-out 0s !important',
                    },
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mt: 3, mb: 2 }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                py: 1.5,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#FFFFFF',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '16px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                },
                '&:disabled': {
                  opacity: 0.5,
                  color: '#FFFFFF',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Resetare parolă'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </AuthPageWrapper>
  )
}

export default ResetPasswordForm
